/**
 * The parts every screen of the agents demo is built from — the reference screen's components
 * (buttons, key caps, hairlines, cards, badges, glyphs) made interactive, plus the few it never
 * needed: menus, sheets, dialogs, switches.
 *
 * Corners follow the reference and nothing else: 10 px for a surface (card, composer, menu, dialog),
 * 8 px for what you press or type in (button, tab, row, field), 6 px for a tile or a small chip,
 * 4 px for a key cap. Only a true circle is round (a dot, a face, a switch).
 */
import { useEffect, useId, useLayoutEffect, useRef, useState, type ButtonHTMLAttributes, type ComponentType, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react'
import { ArrowLeft, Check, ChevronRight, X } from 'lucide-react'
import { create } from 'zustand'

export type Icon = ComponentType<{ size?: number; strokeWidth?: number; className?: string }>
export const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(' ')

/** Every line icon: 16 px, the same stroke. */
export const I = ({ icon: C, className, size = 16 }: { icon: Icon; className?: string; size?: number }) => <C size={size} strokeWidth={2} className={cx('shrink-0', className)} />

/* ── buttons ─────────────────────────────────────────────────────────── */

type BtnProps = ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'ghost' | 'white' | 'outline' | 'danger'; icon?: Icon; iconSize?: number; gap?: number; onPanel?: boolean }
const BTN = {
  ghost: 'px-2 text-ws-fg-2 hover:text-ws-fg',
  white: 'px-3 bg-white font-medium text-black hover:bg-[#e2e2e2] disabled:bg-[#3a3a3a] disabled:text-[#8a8a8a]',
  outline: 'px-3 border border-ws-edge text-ws-fg-2 hover:text-ws-fg',
  danger: 'px-3 border border-ws-edge text-ws-down',
}
export function Btn({ variant = 'ghost', icon, iconSize, gap = 8, onPanel, className, children, ...rest }: BtnProps) {
  return (
    <button
      type="button"
      className={cx('flex h-8 shrink-0 items-center justify-center whitespace-nowrap rounded-[8px] transition-colors duration-150', BTN[variant], variant !== 'white' && (onPanel ? 'hover:bg-ws-raise' : 'hover:bg-ws-hover'), className)}
      style={{ gap }}
      {...rest}
    >
      {icon && <I icon={icon} size={iconSize} className={variant === 'white' ? '' : variant === 'danger' ? '' : 'text-ws-icon'} />}
      {children}
    </button>
  )
}
export function IconBtn({ icon, label, onPanel, className, size, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { icon: Icon; label: string; onPanel?: boolean; size?: number }) {
  return (
    <button type="button" aria-label={label} title={label} className={cx('flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] text-ws-icon transition-colors duration-150 hover:text-ws-fg', onPanel ? 'hover:bg-ws-raise' : 'hover:bg-ws-hover', className)} {...rest}>
      <I icon={icon} size={size} />
    </button>
  )
}

export const Rule = ({ className }: { className?: string }) => <span className={cx('h-4 w-px shrink-0 bg-ws-line', className)} />

/** A key cap: raised on the page, sunk into a selected row. */
export function KeyCap({ children, sunk }: { children: ReactNode; sunk?: boolean }) {
  return <kbd className={cx('flex h-4 min-w-4 items-center justify-center rounded-[4px] px-[3px] font-[inherit] text-[10.5px] font-medium leading-none', sunk ? 'bg-black/25 text-[#b4b4b4]' : 'bg-ws-key text-[#c0c0c0]')}>{children}</kbd>
}

/* ── small pieces ────────────────────────────────────────────────────── */

/** The reference's quiet tag ("Essential"): a dark well on a card, nothing else. A state is never a tag — see Status. */
export function Badge({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cx('inline-flex h-[18px] shrink-0 items-center rounded-full bg-[#0d0d0d] px-[8px] text-[10.5px] leading-none tracking-normal text-[#8e8e8e]', className)}>{children}</span>
}
/** A state, said plainly: a dot and a word. Colour only on the dot. */
export function Status({ tone, children, className }: { tone: 'free' | 'busy' | 'warn' | 'off' | 'down'; children: ReactNode; className?: string }) {
  return (
    <span className={cx('inline-flex shrink-0 items-center gap-[6px] text-[12px] leading-4 tracking-normal', tone === 'off' ? 'text-ws-fg-3' : 'text-ws-fg-2', className)}>
      <Dot tone={tone} />
      {children}
    </span>
  )
}

/** A status dot: free, busy (breathing), waiting for the user. */
export function Dot({ tone, className }: { tone: 'free' | 'busy' | 'warn' | 'off' | 'down'; className?: string }) {
  const c = { free: 'bg-ws-up', busy: 'bg-ws-fg animate-ws-pulse', warn: 'bg-ws-warn', off: 'bg-ws-fg-5', down: 'bg-ws-down' }[tone]
  return <span className={cx('block h-[6px] w-[6px] shrink-0 rounded-full', c, className)} />
}

export function Switch({ on, onChange, label, disabled }: { on: boolean; onChange?: (v: boolean) => void; label: string; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation()
        onChange?.(!on)
      }}
      className={cx('relative h-[18px] w-[30px] shrink-0 rounded-full transition-colors duration-150', on ? 'bg-white' : 'bg-[#2a2a2a]', disabled && 'opacity-50')}
    >
      <span className={cx('absolute top-[2px] h-[14px] w-[14px] rounded-full transition-all duration-150', on ? 'left-[14px] bg-black' : 'left-[2px] bg-[#8a8a8a]')} />
    </button>
  )
}

/** One choice out of a few, as a row of tabs (the agent tabs' own look). */
export function Tabs<T extends string>({ options, value, onChange, className, onPanel }: { options: { id: T; label: string; count?: number; icon?: ReactNode }[]; value: T; onChange: (v: T) => void; className?: string; onPanel?: boolean }) {
  return (
    <div className={cx('ws-noscroll flex min-w-0 items-center gap-2 overflow-x-auto', className)}>
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          onClick={() => onChange(o.id)}
          className={cx(
            'flex h-8 shrink-0 items-center gap-2 rounded-[8px] px-3 tracking-[-0.02em] transition-colors duration-150',
            value === o.id ? cx('font-medium text-ws-fg', onPanel ? 'bg-ws-raise' : 'bg-ws-panel') : cx('text-ws-fg-2 hover:text-ws-fg', onPanel ? 'hover:bg-ws-raise' : 'hover:bg-ws-hover'),
          )}
        >
          {o.icon}
          {o.label}
          {o.count != null && <span className="text-ws-fg-3">{o.count}</span>}
        </button>
      ))}
    </div>
  )
}

export function Card({ className, children, onClick, as = 'div', menu }: { className?: string; children: ReactNode; onClick?: () => void; as?: 'div' | 'button'; menu?: () => PopEntry[] }) {
  const C = as
  return (
    <C onClick={onClick} {...(menu ? menuOn(menu) : {})} className={cx('rounded-[10px] border border-ws-edge bg-ws-panel text-left', as === 'button' ? 'flex flex-col items-stretch justify-start' : 'block', onClick && 'transition-colors duration-150 hover:border-[#303030] hover:bg-[#1a1a1a]', className)}>
      {children}
    </C>
  )
}

/** A section's heading row, as above the reference's shelves. */
export function SectionHead({ title, count, children, className }: { title: string; count?: number; children?: ReactNode; className?: string }) {
  return (
    <div className={cx('flex h-8 items-center pl-2 pr-1', className)}>
      <h2 className="m-0 truncate text-[14px] font-normal leading-5 tracking-normal text-ws-fg">
        {title}
        {count != null && <span className="text-ws-fg-3"> ({count})</span>}
      </h2>
      <div className="ml-auto flex shrink-0 items-center">{children}</div>
    </div>
  )
}

/* ── things that open ────────────────────────────────────────────────── */

/** Close on a press outside or on Escape. */
function useDismiss(open: boolean, close: () => void) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const down = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) close()
    }
    const key = (e: KeyboardEvent) => e.key === 'Escape' && close()
    document.addEventListener('pointerdown', down)
    document.addEventListener('keydown', key)
    return () => {
      document.removeEventListener('pointerdown', down)
      document.removeEventListener('keydown', key)
    }
  }, [open, close])
  return ref
}

export interface MenuItem {
  label: string
  icon?: ReactNode
  hint?: string
  checked?: boolean
  disabled?: boolean
  danger?: boolean
  onSelect?: () => void
}
/** A button with a menu under (or over) it. `keep` leaves it open after a choice (for toggles). */
export function Menu({ trigger, items, align = 'left', side = 'bottom', title, keep, width = 220, className }: { trigger: (open: boolean) => ReactNode; items: (MenuItem | 'rule')[]; align?: 'left' | 'right'; side?: 'top' | 'bottom'; title?: string; keep?: boolean; width?: number; className?: string }) {
  const [open, setOpen] = useState(false)
  const ref = useDismiss(open, () => setOpen(false))
  return (
    <div ref={ref} className={cx('relative', className)}>
      <div onClick={() => setOpen((o) => !o)}>{trigger(open)}</div>
      {open && (
        <div
          role="menu"
          style={{ width }}
          className={cx('absolute z-40 animate-ws-pop rounded-[10px] border border-[#2a2a2a] bg-[#141414] p-1 shadow-[0_12px_32px_rgba(0,0,0,0.6)]', align === 'right' ? 'right-0' : 'left-0', side === 'top' ? 'bottom-[calc(100%+6px)]' : 'top-[calc(100%+6px)]')}
        >
          {title && <div className="px-2 pb-1 pt-[6px] text-[12px] tracking-normal text-ws-fg-3">{title}</div>}
          {items.map((it, i) =>
            it === 'rule' ? (
              <div key={i} className="my-1 h-px bg-[#222]" />
            ) : (
              <button
                key={i}
                type="button"
                role="menuitem"
                disabled={it.disabled}
                onClick={() => {
                  it.onSelect?.()
                  if (!keep) setOpen(false)
                }}
                className={cx('flex h-8 w-full items-center gap-2 rounded-[6px] px-2 text-left transition-colors duration-100', it.disabled ? 'text-ws-fg-4' : it.danger ? 'text-ws-down hover:bg-ws-raise' : 'text-ws-fg-2 hover:bg-ws-raise hover:text-ws-fg')}
              >
                {it.icon}
                <span className="min-w-0 flex-1 truncate">{it.label}</span>
                {it.hint && <span className="shrink-0 text-[12px] tracking-normal text-ws-fg-3">{it.hint}</span>}
                {it.checked && <Check size={14} strokeWidth={2.2} className="shrink-0 text-ws-fg" />}
              </button>
            ),
          )}
        </div>
      )}
    </div>
  )
}

/** A dialog: centred on a desk, a sheet from the bottom on a phone. */
export function Dialog({ open, onClose, title, children, footer, width = 480 }: { open: boolean; onClose: () => void; title: string; children: ReactNode; footer?: ReactNode; width?: number }) {
  const id = useId()
  useEffect(() => {
    if (!open) return
    const key = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    document.addEventListener('keydown', key)
    return () => document.removeEventListener('keydown', key)
  }, [open, onClose])
  if (!open) return null
  return (
    <div className="ws fixed inset-0 z-50 flex items-end justify-center md:items-center" role="dialog" aria-modal aria-labelledby={id}>
      <div className="absolute inset-0 animate-ws-fade bg-black/70" onClick={onClose} />
      <div style={{ maxWidth: width }} className="relative flex max-h-[92dvh] w-full animate-ws-sheet flex-col rounded-t-[10px] border border-[#2a2a2a] bg-[#111] shadow-[0_24px_64px_rgba(0,0,0,0.7)] md:animate-ws-in md:rounded-[10px]">
        <div className="flex h-12 shrink-0 items-center border-b border-ws-line pl-4 pr-2">
          <h2 id={id} className="m-0 text-[14px] font-medium text-ws-fg">
            {title}
          </h2>
          <IconBtn icon={X} label="Close" onPanel onClick={onClose} className="ml-auto" />
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-4">{children}</div>
        {footer && <div className="flex shrink-0 items-center justify-end gap-2 border-t border-ws-line px-4 py-3 pb-[max(12px,env(safe-area-inset-bottom))]">{footer}</div>}
      </div>
    </div>
  )
}

/** A panel that slides in from an edge and covers the screen behind it (phones). */
export function Sheet({ open, onClose, side, children, className }: { open: boolean; onClose: () => void; side: 'left' | 'right' | 'bottom'; children: ReactNode; className?: string }) {
  useEffect(() => {
    if (!open) return
    const key = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    document.addEventListener('keydown', key)
    return () => document.removeEventListener('keydown', key)
  }, [open, onClose])
  if (!open) return null
  const place = { left: 'left-0 top-0 h-full animate-ws-left', right: 'right-0 top-0 h-full animate-ws-right', bottom: 'bottom-0 left-0 w-full animate-ws-sheet rounded-t-[10px]' }[side]
  return (
    <div className="ws fixed inset-0 z-50">
      <div className="absolute inset-0 animate-ws-fade bg-black/70" onClick={onClose} />
      <div className={cx('absolute bg-ws-bg shadow-[0_0_48px_rgba(0,0,0,0.7)]', place, className)}>{children}</div>
    </div>
  )
}

/* ── the menu under the pointer ──────────────────────────────────────── */

/** A row of the menu that opens on a right-click, a long press, or a "…" button. */
export interface PopItem {
  label: string
  icon?: ReactNode
  hint?: string
  checked?: boolean
  disabled?: boolean
  danger?: boolean
  /** Stay open after choosing — for ticking several rows of a list. */
  keep?: boolean
  /** An action at the foot of a list: the list's search never hides it. */
  fixed?: boolean
  onSelect?: () => void
  /** A list that opens beside the row. Built when it opens, so it is always current. */
  sub?: () => PopEntry[]
}
export type PopEntry = PopItem | 'rule'
const usePop = create<{ at: { x: number; y: number } | null; build: () => PopEntry[] }>(() => ({ at: null, build: () => [] }))

/** Open the menu at the pointer, or just under an element. */
export function popAt(where: { clientX: number; clientY: number } | Element, build: () => PopEntry[]) {
  const r = where instanceof Element ? where.getBoundingClientRect() : null
  usePop.setState({ at: r ? { x: r.left, y: r.bottom + 6 } : { x: (where as { clientX: number }).clientX, y: (where as { clientY: number }).clientY }, build })
}
let pressTimer = 0
/** The handlers that give an element its menu: right-click with a mouse, a long press with a finger. */
export function menuOn(build: () => PopEntry[]) {
  const clear = () => window.clearTimeout(pressTimer)
  return {
    onContextMenu: (e: ReactMouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      clear()
      popAt(e, build)
    },
    onPointerDown: (e: ReactPointerEvent) => {
      if (e.pointerType !== 'touch') return
      const { clientX, clientY } = e
      clear()
      pressTimer = window.setTimeout(() => {
        popAt({ clientX, clientY }, build)
        // the finger lifting must not also press what is under it
        const stop = (c: Event) => (c.stopPropagation(), c.preventDefault())
        document.addEventListener('click', stop, { capture: true, once: true })
        window.setTimeout(() => document.removeEventListener('click', stop, true), 900)
      }, 480)
    },
    onPointerUp: clear,
    onPointerCancel: clear,
    onPointerLeave: clear,
  }
}

const POP = 'ws fixed z-[56] w-[220px] animate-ws-pop rounded-[10px] border border-[#2a2a2a] bg-[#141414] p-1 shadow-[0_12px_32px_rgba(0,0,0,0.6)]'
/** Keep a menu wholly on the screen. */
const fit = (x: number, y: number, el: HTMLElement) => ({ x: Math.max(8, Math.min(x, window.innerWidth - el.offsetWidth - 8)), y: Math.max(8, Math.min(y, window.innerHeight - el.offsetHeight - 8)) })

/** Draws the open menu. One of these sits at the top of the screen tree. */
export function PopHost() {
  const at = usePop((s) => s.at)
  const build = usePop((s) => s.build)
  const [, tick] = useState(0)
  const [sub, setSub] = useState<number | null>(null)
  const [q, setQ] = useState('')
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null)
  const [side, setSide] = useState<{ x: number; y: number } | null>(null)
  const main = useRef<HTMLDivElement>(null)
  const list = useRef<HTMLDivElement>(null)
  const narrow = typeof window !== 'undefined' && window.innerWidth < 640
  const close = () => usePop.setState({ at: null })

  useEffect(() => (setSub(null), setQ(''), setPos(null), setSide(null)), [at])
  useEffect(() => {
    if (!at) return
    const down = (e: PointerEvent) => !(e.target as Element).closest?.('[data-pop]') && close()
    const key = (e: KeyboardEvent) => e.key === 'Escape' && close()
    document.addEventListener('pointerdown', down, true)
    document.addEventListener('keydown', key)
    window.addEventListener('resize', close)
    window.addEventListener('blur', close)
    return () => {
      document.removeEventListener('pointerdown', down, true)
      document.removeEventListener('keydown', key)
      window.removeEventListener('resize', close)
      window.removeEventListener('blur', close)
    }
  }, [at])
  useLayoutEffect(() => {
    if (at && main.current) setPos(fit(at.x, at.y, main.current))
  }, [at, sub, narrow])
  // The list beside a row: to its right, or to its left when the screen ends there.
  useLayoutEffect(() => {
    if (sub == null || narrow || !main.current || !list.current) return
    const row = main.current.querySelectorAll('[data-row]')[sub]?.getBoundingClientRect()
    const m = main.current.getBoundingClientRect()
    if (!row) return
    const right = m.right + 4
    setSide(fit(right + list.current.offsetWidth + 8 > window.innerWidth ? m.left - list.current.offsetWidth - 4 : right, row.top - 5, list.current))
  }, [sub, narrow, pos, q])
  if (!at) return null

  const entries = build()
  const parent = sub != null ? (entries.filter((e) => e !== 'rule')[sub] as PopItem | undefined) : undefined
  const all = parent?.sub?.() ?? []
  const long = all.filter((e) => e !== 'rule' && !e.fixed).length > 8
  const inner = long && q.trim() ? all.filter((e) => e === 'rule' || e.fixed || e.label.toLocaleLowerCase('tr').includes(q.trim().toLocaleLowerCase('tr'))) : all
  let n = -1
  const rows = (items: PopEntry[], top: boolean) =>
    items.map((it, i) => {
      if (it === 'rule') return <div key={i} className="my-1 h-px bg-[#222]" />
      const at0 = top ? ++n : -1
      return (
        <button
          key={i}
          type="button"
          role="menuitem"
          data-row={top ? '' : undefined}
          disabled={it.disabled}
          onPointerEnter={() => top && !narrow && setSub(it.sub ? at0 : null)}
          onClick={() => {
            if (it.sub) return (setQ(''), setSub(at0))
            it.onSelect?.()
            if (it.keep) tick((v) => v + 1)
            else close()
          }}
          className={cx('flex h-8 w-full items-center gap-2 rounded-[6px] px-2 text-left transition-colors duration-100', it.disabled ? 'text-ws-fg-4' : it.danger ? 'text-ws-down hover:bg-ws-raise' : 'text-ws-fg-2 hover:bg-ws-raise hover:text-ws-fg', top && sub === at0 && 'bg-ws-raise text-ws-fg')}
        >
          {it.icon}
          <span className="min-w-0 flex-1 truncate">{it.label}</span>
          {it.hint && <span className="shrink-0 text-[12px] tracking-normal text-ws-fg-3">{it.hint}</span>}
          {it.checked && <Check size={14} strokeWidth={2.2} className="shrink-0 text-ws-fg" />}
          {it.sub && <ChevronRight size={14} strokeWidth={2} className="shrink-0 text-ws-fg-4" />}
        </button>
      )
    })
  const listBody = (
    <>
      {long && <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" aria-label="Search" className="mb-1 h-8 w-full rounded-[6px] bg-ws-field px-2 tracking-normal" />}
      <div className="max-h-[min(336px,60vh)] overflow-y-auto">{rows(inner, false)}</div>
    </>
  )
  return (
    <>
      <div ref={main} data-pop role="menu" className={POP} style={{ left: pos?.x ?? at.x, top: pos?.y ?? at.y, visibility: pos ? 'visible' : 'hidden' }} onContextMenu={(e) => e.preventDefault()}>
        {narrow && parent ? (
          <>
            {/* on a phone the list takes the menu's place */}
            <button type="button" onClick={() => setSub(null)} className="flex h-8 w-full items-center gap-2 rounded-[6px] px-2 text-left text-ws-fg-3 transition-colors hover:bg-ws-raise hover:text-ws-fg">
              <ArrowLeft size={14} strokeWidth={2} />
              {parent.label}
            </button>
            <div className="my-1 h-px bg-[#222]" />
            {listBody}
          </>
        ) : (
          rows(entries, true)
        )}
      </div>
      {!narrow && parent && (
        <div ref={list} data-pop role="menu" className={POP} style={{ left: side?.x ?? 0, top: side?.y ?? 0, visibility: side ? 'visible' : 'hidden' }} onContextMenu={(e) => e.preventDefault()}>
          {listBody}
        </div>
      )}
    </>
  )
}

/* ── a name to type, a thing to be sure of ───────────────────────────── */

type Ask = { kind: 'name'; title: string; value: string; label: string; cta: string; done: (name: string) => void } | { kind: 'sure'; title: string; body: string; cta: string; done: () => void }
const useAsk = create<{ ask: Ask | null }>(() => ({ ask: null }))
export const askName = (o: { title: string; value?: string; label?: string; cta: string; done: (name: string) => void }) => useAsk.setState({ ask: { kind: 'name', value: '', label: 'Name', ...o } })
export const askSure = (o: { title: string; body: string; cta: string; done: () => void }) => useAsk.setState({ ask: { kind: 'sure', ...o } })

export function AskHost() {
  const ask = useAsk((s) => s.ask)
  const [text, setText] = useState('')
  useEffect(() => setText(ask?.kind === 'name' ? ask.value : ''), [ask])
  const close = () => useAsk.setState({ ask: null })
  const ok = ask?.kind === 'sure' || !!text.trim()
  const done = () => {
    if (!ask || !ok) return
    close()
    if (ask.kind === 'name') ask.done(text.trim())
    else ask.done()
  }
  return (
    <Dialog
      open={!!ask}
      onClose={close}
      title={ask?.title ?? ''}
      width={400}
      footer={
        <>
          <Btn variant="outline" onPanel onClick={close}>
            Cancel
          </Btn>
          <Btn variant={ask?.kind === 'sure' ? 'danger' : 'white'} onPanel disabled={!ok} onClick={done}>
            {ask?.cta}
          </Btn>
        </>
      }
    >
      {ask?.kind === 'name' ? (
        <Field label={ask.label}>
          <input autoFocus onFocus={(e) => e.currentTarget.select()} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && done()} className={inputCls} />
        </Field>
      ) : (
        <p className="m-0 leading-[21px] tracking-normal text-ws-fg-2">{ask?.body}</p>
      )}
    </Dialog>
  )
}

/* ── fields ──────────────────────────────────────────────────────────── */

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-[6px] flex items-baseline justify-between text-[12px] tracking-normal text-ws-label">
        {label}
        {hint && <span className="text-ws-fg-4">{hint}</span>}
      </span>
      {children}
    </label>
  )
}
export const inputCls = 'h-9 w-full rounded-[8px] border border-ws-line bg-ws-field px-[10px] transition-colors duration-150 focus:border-ws-focus'

/* ── glyphs: an agent's face ─────────────────────────────────────────── */

export type Palette = 'rune' | 'aether' | 'syntax' | 'theo' | 'clara' | 'mint' | 'amber' | 'coral'
const tint = (c: string) => Array<string>(8).fill(c)
const GLYPHS: Record<Palette, { ring: string[]; core: [string, string, string, string] }> = {
  rune: { ring: ['#9fe8c8', '#fbf7e6', '#ffd29a', '#fcf4e2', '#b6efcc', '#f0eef8', '#bfaef8', '#aec2f8'], core: ['#9868d2', '#ee82b4', '#ffae74', '#ffeaa6'] },
  aether: { ring: tint('#e6d2dc'), core: ['#f9cbe2', '#f5b4d4', '#eea8ca', '#e6a3c6'] },
  syntax: { ring: tint('#d0d6d4'), core: ['#ffffff', '#ececf0', '#d6dad9', '#c8cecd'] },
  theo: { ring: ['#e6e8c0', ...tint('#d0d6da').slice(1)], core: ['#e9f5ea', '#b4d8e6', '#8cc0e0', '#7fb2d8'] },
  clara: { ring: tint('#d4cde6'), core: ['#e8d4fc', '#d2a8f4', '#c09ee8', '#b89be0'] },
  mint: { ring: tint('#cfe6dc'), core: ['#e6fbf0', '#a8e6c8', '#7fd4b0', '#6cc4a2'] },
  amber: { ring: tint('#eadfc8'), core: ['#fff3d6', '#ffd894', '#f6bc6a', '#eaa85a'] },
  coral: { ring: tint('#ecd2cc'), core: ['#ffe2d8', '#ffaf9a', '#f48c7a', '#e87c6e'] },
}
export const PALETTES = Object.keys(GLYPHS) as Palette[]

/** The agent glyph of the reference: eight beads around a glowing core. */
export function Glyph({ palette, size = 16, dim, className }: { palette: Palette; size?: number; dim?: boolean; className?: string }) {
  const id = useId()
  const a = GLYPHS[palette]
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden className={cx('shrink-0', className)} opacity={dim ? 0.72 : 1}>
      <defs>
        <linearGradient id={id} x1="0.36" y1="0" x2="0.64" y2="1">
          <stop offset="0" stopColor={a.core[0]} />
          <stop offset="0.38" stopColor={a.core[1]} />
          <stop offset="0.68" stopColor={a.core[2]} />
          <stop offset="1" stopColor={a.core[3]} />
        </linearGradient>
      </defs>
      {a.ring.map((c, i) => (
        <circle key={i} cx={8} cy={2} r={1.05} fill={c} transform={`rotate(${i * 45} 8 8)`} />
      ))}
      <circle cx={8} cy={8} r={3.7} fill={`url(#${id})`} />
    </svg>
  )
}

/** A group's face: its members' glyphs, overlapping. `on` = the surface it sits on, so each glyph cuts cleanly into the next. */
export function Stack({ palettes, max = 3, on = 'bg-ws-bg', dim, pulse }: { palettes: Palette[]; max?: number; on?: string; dim?: boolean; pulse?: boolean[] }) {
  const list = palettes.slice(0, max)
  if (!list.length) return <span className="block h-4 w-4 shrink-0 rounded-full border border-dashed border-ws-fg-5" />
  return (
    <span className="flex h-4 shrink-0 items-center">
      {list.map((p, i) => (
        <span key={i} className={cx('flex h-4 w-4 items-center justify-center rounded-full', on)} style={{ marginLeft: i ? -6 : 0, zIndex: list.length - i }}>
          <Glyph palette={p} dim={dim} className={pulse?.[i] ? 'animate-ws-pulse' : undefined} />
        </span>
      ))}
    </span>
  )
}

/** Several agents at once: their glyphs side by side. */
export function Glyphs({ palettes, max = 5 }: { palettes: Palette[]; max?: number }) {
  return (
    <span className="flex h-4 items-center gap-1">
      {palettes.slice(0, max).map((p, i) => (
        <Glyph key={i} palette={p} />
      ))}
    </span>
  )
}

/** A square tile with a mark in it — the app logos' frame. */
export function Tile({ children, bg, className, size = 24 }: { children: ReactNode; bg: string; className?: string; size?: number }) {
  return (
    <span className={cx('flex shrink-0 items-center justify-center rounded-[6px]', className)} style={{ width: size, height: size, background: bg }} aria-hidden>
      {children}
    </span>
  )
}

/** Money and signed changes, tabular. */
export const usd = (n: number, digits = 2) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: digits, maximumFractionDigits: digits })
export const signed = (n: number, digits = 2) => `${n >= 0 ? '+' : '−'}${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })}`
export const pct = (n: number) => `${n >= 0 ? '+' : '−'}${Math.abs(n).toFixed(2)}%`
export const toneOf = (n: number) => (n > 0 ? 'text-ws-up' : n < 0 ? 'text-ws-down' : 'text-ws-fg-3')
